import { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { socSeverityColor, socStatusColor, socStatusLabel, fmtISTDate } from "@/lib/utils";
import { FileCsv, FilePdf, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const Kpi = ({ label, value }) => (
  <Card className="p-4 border-border rounded-sm" data-testid={`soc-rep-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
    <div className="font-display text-2xl font-bold">{value}</div>
  </Card>
);

export default function SocReports() {
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [allFields, setAllFields] = useState([]);
  const [selFields, setSelFields] = useState([]);
  useEffect(() => {
    api.get("/soc/report/fields").then(r => { setAllFields(r.data.fields); setSelFields(r.data.default); }).catch(() => {});
  }, []);
  const toggleField = (k) => setSelFields(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    p.set("client", client);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    api.get(`/soc/report?${p.toString()}`).then(r => setData(r.data)).catch(() => {});
  }, [client, from, to]);

  useEffect(() => { api.get("/client-registry?module=soc").then(r => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const exportFile = async (fmt) => {
    const p = new URLSearchParams();
    p.set("client", client);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    if (fmt === "csv" && selFields.length) p.set("fields", selFields.join(","));
    try {
      const token = localStorage.getItem("helix_token");
      const res = await fetch(`${API}/soc/report/${fmt}?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `soc-report.${fmt === "csv" ? "csv" : "pdf"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  const s = data?.summary || {};

  return (
    <div className="p-6 space-y-5" data-testid="soc-reports-view">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-rag-red mb-1">Security Operations</div>
        <h1 className="font-display text-3xl font-black flex items-center gap-2"><FileText size={26} /> SOC Reports</h1>
      </div>

      <Card className="p-3 border-border rounded-sm flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] font-mono uppercase text-muted-foreground">Client</label>
          <Select value={client} onValueChange={setClient}>
            <SelectTrigger data-testid="soc-rep-client" className="w-48 rounded-sm h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All clients</SelectItem>{clients.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase text-muted-foreground">From</label>
          <Input data-testid="soc-rep-from" type="date" value={from} onChange={e => setFrom(e.target.value)} className="rounded-sm h-9 text-sm mt-1 w-40" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase text-muted-foreground">To</label>
          <Input data-testid="soc-rep-to" type="date" value={to} onChange={e => setTo(e.target.value)} className="rounded-sm h-9 text-sm mt-1 w-40" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button data-testid="soc-export-csv" onClick={() => exportFile("csv")} variant="outline" className="rounded-sm font-mono uppercase text-xs"><FileCsv size={15} className="mr-1" /> CSV</Button>
          <Button data-testid="soc-export-pdf" onClick={() => exportFile("pdf")} variant="outline" className="rounded-sm font-mono uppercase text-xs"><FilePdf size={15} className="mr-1" /> PDF</Button>
        </div>
      </Card>

      <Card className="p-3 border-border rounded-sm" data-testid="soc-report-fields">
        <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">CSV columns</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {allFields.map(f => (
            <label key={f.key} className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" data-testid={`soc-field-${f.key}`} checked={selFields.includes(f.key)} onChange={() => toggleField(f.key)} />
              {f.label}
            </label>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi label="Total" value={s.total ?? 0} />
        <Kpi label="Open" value={s.open ?? 0} />
        <Kpi label="Closed" value={s.closed ?? 0} />
        <Kpi label="SLA Breached" value={s.resolution_breached ?? 0} />
        <Kpi label="MTTD (h)" value={s.mttd_hours ?? 0} />
        <Kpi label="MTTA (h)" value={s.mtta_hours ?? 0} />
        <Kpi label="MTTR (h)" value={s.mttr_hours ?? 0} />
      </div>

      <Card className="border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="soc-report-table">
          <thead className="bg-accent/30 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2.5">Number</th>
              <th className="text-left px-3 py-2.5">Title</th>
              <th className="text-left px-3 py-2.5">Client</th>
              <th className="text-left px-3 py-2.5">Severity</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-left px-3 py-2.5">Classification</th>
              <th className="text-left px-3 py-2.5">Created</th>
            </tr>
          </thead>
          <tbody>
            {(data?.cases || []).map(c => (
              <tr key={c.id} className="border-t border-border">
                <td className="px-3 py-2.5 font-mono text-primary">{c.number}</td>
                <td className="px-3 py-2.5 max-w-[260px] truncate">{c.title}</td>
                <td className="px-3 py-2.5 text-xs">{c.client}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] font-mono px-2 py-0.5 border ${socSeverityColor(c.severity)}`}>{c.severity}</span></td>
                <td className="px-3 py-2.5"><span className={`text-[10px] font-mono px-2 py-0.5 border ${socStatusColor(c.status)}`}>{socStatusLabel(c.status)}</span></td>
                <td className="px-3 py-2.5 text-xs">{c.classification}</td>
                <td className="px-3 py-2.5 text-xs font-mono">{fmtISTDate(c.created_at)}</td>
              </tr>
            ))}
            {(data?.cases || []).length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-muted-foreground">No cases in range.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
